// ===========================================
// Country and State/Province Reference Data
// ===========================================
// Static fallback data - prefer using useCountries() hook for dynamic data
// The hook fetches from /api/public/countries and caches for 24 hours
// This static data is kept for:
// 1. Helper functions that need synchronous access (formatAddress, getCountry)
// 2. Fallback when API is unavailable
// 3. Type definitions

export interface StateProvince {
  code: string;
  name: string;
}

export interface Country {
  code: string;
  name: string;
  stateLabel: string;
  postcodeLabel: string;
  states: StateProvince[];
}

export const COUNTRIES: Country[] = [
  {
    code: 'AU',
    name: 'Australia',
    stateLabel: 'State',
    postcodeLabel: 'Postcode',
    states: [
      { code: 'NSW', name: 'New South Wales' },
      { code: 'VIC', name: 'Victoria' },
      { code: 'QLD', name: 'Queensland' },
      { code: 'WA', name: 'Western Australia' },
      { code: 'SA', name: 'South Australia' },
      { code: 'TAS', name: 'Tasmania' },
      { code: 'ACT', name: 'Australian Capital Territory' },
      { code: 'NT', name: 'Northern Territory' },
    ],
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    stateLabel: 'Region',
    postcodeLabel: 'Postcode',
    states: [
      { code: 'AUK', name: 'Auckland' },
      { code: 'WGN', name: 'Wellington' },
      { code: 'CAN', name: 'Canterbury' },
      { code: 'WKO', name: 'Waikato' },
      { code: 'BOP', name: 'Bay of Plenty' },
      { code: 'OTA', name: 'Otago' },
      { code: 'MWT', name: 'Manawatū-Whanganui' },
      { code: 'HKB', name: "Hawke's Bay" },
      { code: 'TKI', name: 'Taranaki' },
      { code: 'NTL', name: 'Northland' },
      { code: 'STL', name: 'Southland' },
      { code: 'GIS', name: 'Gisborne' },
      { code: 'MBH', name: 'Marlborough' },
      { code: 'NSN', name: 'Nelson' },
      { code: 'TAS', name: 'Tasman' },
      { code: 'WTC', name: 'West Coast' },
    ],
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    stateLabel: 'Country/County',
    postcodeLabel: 'Postcode',
    states: [
      { code: 'ENG', name: 'England' },
      { code: 'SCT', name: 'Scotland' },
      { code: 'WLS', name: 'Wales' },
      { code: 'NIR', name: 'Northern Ireland' },
    ],
  },
  {
    code: 'US',
    name: 'United States',
    stateLabel: 'State',
    postcodeLabel: 'ZIP Code',
    states: [
      { code: 'AL', name: 'Alabama' },
      { code: 'AK', name: 'Alaska' },
      { code: 'AZ', name: 'Arizona' },
      { code: 'AR', name: 'Arkansas' },
      { code: 'CA', name: 'California' },
      { code: 'CO', name: 'Colorado' },
      { code: 'CT', name: 'Connecticut' },
      { code: 'DE', name: 'Delaware' },
      { code: 'DC', name: 'District of Columbia' },
      { code: 'FL', name: 'Florida' },
      { code: 'GA', name: 'Georgia' },
      { code: 'HI', name: 'Hawaii' },
      { code: 'ID', name: 'Idaho' },
      { code: 'IL', name: 'Illinois' },
      { code: 'IN', name: 'Indiana' },
      { code: 'IA', name: 'Iowa' },
      { code: 'KS', name: 'Kansas' },
      { code: 'KY', name: 'Kentucky' },
      { code: 'LA', name: 'Louisiana' },
      { code: 'ME', name: 'Maine' },
      { code: 'MD', name: 'Maryland' },
      { code: 'MA', name: 'Massachusetts' },
      { code: 'MI', name: 'Michigan' },
      { code: 'MN', name: 'Minnesota' },
      { code: 'MS', name: 'Mississippi' },
      { code: 'MO', name: 'Missouri' },
      { code: 'MT', name: 'Montana' },
      { code: 'NE', name: 'Nebraska' },
      { code: 'NV', name: 'Nevada' },
      { code: 'NH', name: 'New Hampshire' },
      { code: 'NJ', name: 'New Jersey' },
      { code: 'NM', name: 'New Mexico' },
      { code: 'NY', name: 'New York' },
      { code: 'NC', name: 'North Carolina' },
      { code: 'ND', name: 'North Dakota' },
      { code: 'OH', name: 'Ohio' },
      { code: 'OK', name: 'Oklahoma' },
      { code: 'OR', name: 'Oregon' },
      { code: 'PA', name: 'Pennsylvania' },
      { code: 'RI', name: 'Rhode Island' },
      { code: 'SC', name: 'South Carolina' },
      { code: 'SD', name: 'South Dakota' },
      { code: 'TN', name: 'Tennessee' },
      { code: 'TX', name: 'Texas' },
      { code: 'UT', name: 'Utah' },
      { code: 'VT', name: 'Vermont' },
      { code: 'VA', name: 'Virginia' },
      { code: 'WA', name: 'Washington' },
      { code: 'WV', name: 'West Virginia' },
      { code: 'WI', name: 'Wisconsin' },
      { code: 'WY', name: 'Wyoming' },
    ],
  },
  {
    code: 'CA',
    name: 'Canada',
    stateLabel: 'Province',
    postcodeLabel: 'Postal Code',
    states: [
      { code: 'AB', name: 'Alberta' },
      { code: 'BC', name: 'British Columbia' },
      { code: 'MB', name: 'Manitoba' },
      { code: 'NB', name: 'New Brunswick' },
      { code: 'NL', name: 'Newfoundland and Labrador' },
      { code: 'NS', name: 'Nova Scotia' },
      { code: 'NT', name: 'Northwest Territories' },
      { code: 'NU', name: 'Nunavut' },
      { code: 'ON', name: 'Ontario' },
      { code: 'PE', name: 'Prince Edward Island' },
      { code: 'QC', name: 'Quebec' },
      { code: 'SK', name: 'Saskatchewan' },
      { code: 'YT', name: 'Yukon' },
    ],
  },
  {
    code: 'SG',
    name: 'Singapore',
    stateLabel: 'District',
    postcodeLabel: 'Postal Code',
    states: [],
  },
  {
    code: 'HK',
    name: 'Hong Kong',
    stateLabel: 'District',
    postcodeLabel: 'Postal Code',
    states: [
      { code: 'HKI', name: 'Hong Kong Island' },
      { code: 'KLN', name: 'Kowloon' },
      { code: 'NT', name: 'New Territories' },
    ],
  },
  {
    code: 'IE',
    name: 'Ireland',
    stateLabel: 'County',
    postcodeLabel: 'Eircode',
    states: [
      { code: 'CW', name: 'Carlow' },
      { code: 'CN', name: 'Cavan' },
      { code: 'CE', name: 'Clare' },
      { code: 'CO', name: 'Cork' },
      { code: 'DL', name: 'Donegal' },
      { code: 'D', name: 'Dublin' },
      { code: 'G', name: 'Galway' },
      { code: 'KY', name: 'Kerry' },
      { code: 'KE', name: 'Kildare' },
      { code: 'KK', name: 'Kilkenny' },
      { code: 'LS', name: 'Laois' },
      { code: 'LM', name: 'Leitrim' },
      { code: 'LK', name: 'Limerick' },
      { code: 'LD', name: 'Longford' },
      { code: 'LH', name: 'Louth' },
      { code: 'MO', name: 'Mayo' },
      { code: 'MH', name: 'Meath' },
      { code: 'MN', name: 'Monaghan' },
      { code: 'OY', name: 'Offaly' },
      { code: 'RN', name: 'Roscommon' },
      { code: 'SO', name: 'Sligo' },
      { code: 'TA', name: 'Tipperary' },
      { code: 'WD', name: 'Waterford' },
      { code: 'WH', name: 'Westmeath' },
      { code: 'WX', name: 'Wexford' },
      { code: 'WW', name: 'Wicklow' },
    ],
  },
];

// Helper functions
export function getCountry(code: string): Country | undefined {
  return COUNTRIES.find((c) => c.code === code);
}

export function getCountryStates(countryCode: string): StateProvince[] {
  return getCountry(countryCode)?.states || [];
}

export function getStateName(countryCode: string, stateCode: string): string | undefined {
  const country = getCountry(countryCode);
  return country?.states.find((s) => s.code === stateCode)?.name;
}

export function getCountryName(code: string): string | undefined {
  return getCountry(code)?.name;
}

/**
 * Format an address for display
 */
export function formatAddress(address: {
  unit?: string | null;
  streetAddress?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country: string;
}): string {
  const parts: string[] = [];

  if (address.unit && address.streetAddress) {
    parts.push(`${address.unit}, ${address.streetAddress}`);
  } else if (address.streetAddress) {
    parts.push(address.streetAddress);
  } else if (address.unit) {
    parts.push(address.unit);
  }

  if (address.suburb) {
    let cityLine = address.suburb;
    if (address.state) {
      cityLine += ` ${address.state}`;
    }
    if (address.postcode) {
      cityLine += ` ${address.postcode}`;
    }
    parts.push(cityLine);
  } else if (address.state || address.postcode) {
    parts.push([address.state, address.postcode].filter(Boolean).join(' '));
  }

  const country = getCountry(address.country);
  if (country && address.country !== 'AU') {
    parts.push(country.name);
  }

  return parts.join(', ');
}
